import React, { useEffect, useState, useCallback } from "react";
import apiClient from "../apiClient";
import { toast } from "react-toastify";

const STATUS_COLORS = {
  Pending:  { bg: "#FEF3C7", color: "#92400E", dot: "#F59E0B" },
  Approved: { bg: "#D1FAE5", color: "#065F46", dot: "#10B981" },
  Rejected: { bg: "#FEE2E2", color: "#7F1D1D", dot: "#DC2626" },
  Partial:  { bg: "#EEF2FF", color: "#312E81", dot: "#6366F1" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.Pending;
  return (
    <span style={{
      background: s.bg, color: s.color, borderRadius: 20,
      padding: "2px 10px", fontSize: 10, fontWeight: 800,
      display: "inline-flex", alignItems: "center", gap: 4,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, display: "inline-block" }} />
      {status}
    </span>
  );
};

/* productDecisions[key][productId] = { status: 'pending'|'approved'|'rejected', qty, reason } */

const PendingTransferPopup = ({ shopId }) => {
  const [pendingTransfers, setPendingTransfers]   = useState([]);
  const [rejectedTransfers, setRejectedTransfers] = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [visible, setVisible]                     = useState(false);
  const [activeTab, setActiveTab]                 = useState("pending");

  const [productDetails, setProductDetails]   = useState({});
  const [productDecisions, setProductDecisions] = useState({});
  const [processingId, setProcessingId]       = useState(null);

  const fetchProducts = useCallback(async (transferId) => {
    if (productDetails[transferId] !== undefined) return;
    try {
      const res = await apiClient.post("/Liquor/GetStgTransferProductsV3", { transferId });
      const prods = res.data?.data || [];
      setProductDetails(prev => ({ ...prev, [transferId]: prods }));
      const initial = {};
      prods.forEach((p, i) => {
        const pid = p.productId || p.ProductId || p.id || p.Id || i;
        initial[pid] = { status: "approved", qty: p.qty ?? p.Qty ?? 0, reason: "" };
      });
      setProductDecisions(prev => ({ ...prev, [transferId]: initial }));
    } catch {
      setProductDetails(prev => ({ ...prev, [transferId]: [] }));
    }
  }, [productDetails]);

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.post("/Liquor/GetPendingTransfers", { shopId: Number(shopId) });
      const data = res.data?.data || [];
      setPendingTransfers(data);
      data.forEach(item => fetchProducts(item.transferId));
      return data;
    } catch { return []; }
    finally { setLoading(false); }
  }, [shopId]);

  const fetchRejected = useCallback(async () => {
    try {
      const res = await apiClient.post("/Liquor/GetRejectedTransfers", { shopId: Number(shopId) });
      const data = res.data?.data || [];
      setRejectedTransfers(data);
      data.forEach(item => fetchProducts(item.transferId));
      return data;
    } catch { setRejectedTransfers([]); return []; }
  }, [shopId]);

  useEffect(() => {
    if (!shopId) return;
    Promise.all([fetchPending(), fetchRejected()]).then(([p, r]) => {
      if (p.length > 0 || r.length > 0) setVisible(true);
    });
  }, [shopId]);

  const handleSelectAll = (transferId, prods, selectAll) => {
    const next = {};
    prods.forEach((p, i) => {
      const pid = p.productId || p.ProductId || p.id || p.Id || i;
      next[pid] = selectAll
        ? { status: "approved", qty: p.qty ?? p.Qty ?? 0, reason: "" }
        : { status: "rejected", qty: 0, reason: "" };
    });
    setProductDecisions(prev => ({ ...prev, [transferId]: next }));
  };

  const setDecision = (transferId, productId, patch) => {
    setProductDecisions(prev => ({
      ...prev,
      [transferId]: {
        ...prev[transferId],
        [productId]: { ...(prev[transferId]?.[productId] || {}), ...patch },
      },
    }));
  };

  const handleSubmit = async (item) => {
    const { transferId } = item;
    const prods     = productDetails[transferId] || [];
    const decisions = productDecisions[transferId] || {};
    const getDetailId = (p, i) =>
      p.detailId || p.DetailId || p.stgDetailId || p.StgDetailId || i;

    const allRejected = prods.every((p, i) => {
      const pid = p.productId || p.ProductId || p.id || p.Id || i;
      return decisions[pid]?.status === "rejected";
    });

    try {
      setProcessingId(transferId);

      if (allRejected) {
        await apiClient.post("/Liquor/ApproveRejectTransferV2", {
          receivedId: transferId,
          action: "Reject",
          rejectReason: "Products rejected",
          partialItems: [],
        });
        toast.success("All products rejected — stock reverted to sender");
      } else {
        const partialItems = prods.map((p, i) => {
          const pid = p.productId || p.ProductId || p.id || p.Id || i;
          const did = getDetailId(p, i);
          const d   = decisions[pid];
          return {
            detailId: did,
            productId: pid,
            acceptedQty: d?.status === "approved" ? Number(d.qty ?? 0) : 0,
          };
        });
        await apiClient.post("/Liquor/ApproveRejectTransferV2", {
          receivedId: transferId,
          action: "Approve",
          rejectReason: "",
          partialItems,
        });
        const rejectedCount = partialItems.filter(x => x.acceptedQty === 0).length;
        toast.success(rejectedCount > 0
          ? `Approved with ${rejectedCount} product(s) returned to sender`
          : "Transfer approved successfully"
        );
      }

      setPendingTransfers(prev => prev.filter(x => x.transferId !== transferId));
      if (pendingTransfers.length - 1 + rejectedTransfers.length === 0) setVisible(false);
    } catch {
      toast.error("Failed to submit decision");
    } finally {
      setProcessingId(null);
    }
  };

  const totalCount = pendingTransfers.length + rejectedTransfers.length;
  if (!visible || totalCount === 0) return null;

  const TAB = [
    { key: "pending",  label: "Pending",  count: pendingTransfers.length },
    { key: "rejected", label: "Rejected", count: rejectedTransfers.length },
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9998,
      background: "rgba(0,0,0,0.50)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%",
        maxWidth: 620, maxHeight: "90vh", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 18px", background: "#1E3A5F",
          borderRadius: "16px 16px 0 0", flexShrink: 0,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>Transfer Status</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>
              {pendingTransfers.length} pending · {rejectedTransfers.length} rejected
            </div>
          </div>
          <button onClick={() => setVisible(false)} style={{
            background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8,
            color: "#fff", width: 32, height: 32, cursor: "pointer", fontSize: 14, fontWeight: 700,
          }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1.5px solid #E5E7EB", flexShrink: 0, background: "#F9FAFB" }}>
          {TAB.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              flex: 1, padding: "10px 0", border: "none", background: "none",
              cursor: "pointer", fontSize: 12, fontWeight: 700,
              color: activeTab === t.key ? "#7B1D4F" : "#6B7280",
              borderBottom: activeTab === t.key ? "2.5px solid #7B1D4F" : "2.5px solid transparent",
            }}>
              {t.label}
              {t.count > 0 && (
                <span style={{
                  marginLeft: 6, background: activeTab === t.key ? "#7B1D4F" : "#9CA3AF",
                  color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10,
                }}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>

          {/* ── PENDING TAB ── */}
          {activeTab === "pending" && (
            pendingTransfers.length === 0
              ? <div style={{ color: "#9CA3AF", fontSize: 13, textAlign: "center", padding: 24 }}>No pending transfers</div>
              : pendingTransfers.map(item => {
                const prods        = productDetails[item.transferId] || [];
                const decisions    = productDecisions[item.transferId] || {};
                const isProcessing = processingId === item.transferId;
                const rejectedCount = prods.filter((p, i) => {
                  const pid = p.productId || p.ProductId || p.id || p.Id || i;
                  return (decisions[pid]?.status ?? "approved") === "rejected";
                }).length;
                const approvedCount = prods.filter((p, i) => {
                  const pid = p.productId || p.ProductId || p.id || p.Id || i;
                  return (decisions[pid]?.status ?? "approved") === "approved";
                }).length;
                const allChecked  = prods.length > 0 && rejectedCount === 0;
                const someChecked = approvedCount > 0 && rejectedCount > 0;
                const canSubmit   = prods.length > 0;

                return (
                  <div key={item.transferId} style={{
                    border: "1.5px solid #E5E7EB", borderRadius: 12, marginBottom: 12, overflow: "hidden",
                  }}>
                    {/* Meta */}
                    <div style={{ padding: "10px 14px", background: "#F0F9FF" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#1E3A5F" }}>
                            To: {item.receiverShopName || item.shopName}
                          </div>
                          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
                            Txn: {item.transactionNumber}
                            {item.remark ? ` · ${item.remark}` : ""}
                          </div>
                        </div>
                        <StatusBadge status="Pending" />
                      </div>
                    </div>

                    {/* Products */}
                    <div style={{ padding: "8px 14px" }}>
                      {prods.length === 0 ? (
                        <div style={{ color: "#9CA3AF", fontSize: 12, padding: "6px 0" }}>Loading products…</div>
                      ) : (
                        <>
                          {/* Header row with Select All checkbox */}
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: "28px 1fr 58px 48px 64px",
                            gap: "0 4px", fontSize: 9, fontWeight: 800, color: "#9CA3AF",
                            textTransform: "uppercase", letterSpacing: 0.5,
                            padding: "4px 6px", background: "#F9FAFB", borderRadius: 6, marginBottom: 4,
                            alignItems: "center",
                          }}>
                            <input
                              type="checkbox"
                              checked={allChecked}
                              ref={el => { if (el) el.indeterminate = someChecked; }}
                              onChange={e => handleSelectAll(item.transferId, prods, e.target.checked)}
                              title="Select All"
                              style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#059669" }}
                            />
                            <span>Product</span>
                            <span style={{ textAlign: "center" }}>Packing</span>
                            <span style={{ textAlign: "center" }}>Sent</span>
                            <span style={{ textAlign: "center" }}>Accept</span>
                          </div>

                          {prods.map((p, i) => {
                            const pid      = p.productId || p.ProductId || p.id || p.Id || i;
                            const sentQty  = p.qty ?? p.Qty ?? 0;
                            const d        = decisions[pid] || { status: "approved", qty: sentQty, reason: "" };
                            const isApproved = d.status === "approved";
                            const isRejected = d.status === "rejected";

                            return (
                              <div key={i}>
                                <div style={{
                                  display: "grid",
                                  gridTemplateColumns: "28px 1fr 58px 48px 64px",
                                  gap: "0 4px", alignItems: "center",
                                  padding: "6px 6px",
                                  borderBottom: "1px solid #F3F4F6",
                                  background: isApproved ? "#F0FDF4" : "#FFF1F1",
                                  borderRadius: 4,
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={isApproved}
                                    onChange={e => setDecision(item.transferId, pid, {
                                      status: e.target.checked ? "approved" : "rejected",
                                      qty: e.target.checked ? sentQty : 0,
                                      reason: "",
                                    })}
                                    style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#059669" }}
                                  />
                                  <span style={{
                                    fontSize: 11, fontWeight: 600,
                                    color: isApproved ? "#111827" : "#7F1D1D",
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                  }}>
                                    {p.productName || p.ProductName || p.pName || p.name}
                                  </span>
                                  <span style={{ fontSize: 10, color: "#6B7280", textAlign: "center" }}>
                                    {p.packing || p.Packing || "—"}
                                  </span>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", textAlign: "center" }}>
                                    {sentQty}
                                  </span>

                                  {/* Accept qty — editable only when approved */}
                                  <input
                                    type="number"
                                    min="0"
                                    max={sentQty}
                                    inputMode="numeric"
                                    disabled={!isApproved}
                                    value={isApproved ? d.qty : ""}
                                    placeholder="—"
                                    onChange={e => {
                                      const val = Math.min(Number(sentQty), Math.max(0, parseInt(e.target.value) || 0));
                                      setDecision(item.transferId, pid, { qty: val });
                                    }}
                                    style={{
                                      width: "100%", padding: "3px 4px", textAlign: "center",
                                      border: `1.5px solid ${isApproved ? "#10B981" : "#E5E7EB"}`,
                                      borderRadius: 6, fontSize: 12, fontWeight: 700,
                                      color: isApproved ? "#065F46" : "#9CA3AF",
                                      background: isApproved ? "#F0FDF4" : "#F9FAFB",
                                      outline: "none", boxSizing: "border-box",
                                    }}
                                  />
                                </div>

                                {/* Per-product reject reason */}
                                {isRejected && (
                                  <div style={{ padding: "4px 6px 6px 6px", background: "#FFF1F1" }}>
                                    <input
                                      type="text"
                                      placeholder="Reason (optional)…"
                                      value={d.reason}
                                      onChange={e => setDecision(item.transferId, pid, { reason: e.target.value })}
                                      style={{
                                        width: "100%", padding: "4px 8px",
                                        border: "1px solid #FCA5A5", borderRadius: 6,
                                        fontSize: 11, color: "#DC2626", outline: "none",
                                        boxSizing: "border-box", background: "#FFF",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>

                    {/* Submit */}
                    <div style={{
                      padding: "10px 14px", borderTop: "1px solid #F3F4F6",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      <span style={{ fontSize: 11, color: rejectedCount > 0 ? "#DC2626" : "#10B981", fontWeight: 600 }}>
                        {rejectedCount > 0
                          ? `${rejectedCount} rejected · ${approvedCount} approved`
                          : `All ${approvedCount} product(s) approved`}
                      </span>
                      <button
                        onClick={() => handleSubmit(item)}
                        disabled={!canSubmit || isProcessing}
                        style={{
                          background: canSubmit ? "#1E3A5F" : "#E5E7EB",
                          border: "none", borderRadius: 8,
                          padding: "7px 18px", fontSize: 12, fontWeight: 700,
                          cursor: canSubmit ? "pointer" : "not-allowed",
                          color: canSubmit ? "#fff" : "#9CA3AF",
                          opacity: isProcessing ? 0.6 : 1,
                        }}
                      >{isProcessing ? "Submitting…" : "Confirm Decision"}</button>
                    </div>
                  </div>
                );
              })
          )}

          {/* ── REJECTED TAB ── */}
          {activeTab === "rejected" && (
            rejectedTransfers.length === 0
              ? <div style={{ color: "#9CA3AF", fontSize: 13, textAlign: "center", padding: 24 }}>No rejected transfers</div>
              : rejectedTransfers.map(item => {
                const prods = productDetails[item.transferId];
                return (
                  <div key={item.transferId} style={{
                    border: "1.5px solid #FCA5A5", borderRadius: 12, marginBottom: 12, overflow: "hidden",
                  }}>
                    <div style={{ padding: "10px 14px", background: "#FFF1F1" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#7F1D1D" }}>
                            To: {item.receiverShopName || item.shopName}
                          </div>
                          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
                            Txn: {item.transactionNumber}{item.remark ? ` · ${item.remark}` : ""}
                          </div>
                          {item.rejectReason && (
                            <div style={{
                              marginTop: 5, fontSize: 11, color: "#DC2626",
                              background: "#FEE2E2", borderRadius: 6, padding: "3px 8px", display: "inline-block",
                            }}>
                              Reason: {item.rejectReason}
                            </div>
                          )}
                        </div>
                        <StatusBadge status="Rejected" />
                      </div>
                    </div>
                    <div style={{ padding: "10px 14px" }}>
                      {!prods ? (
                        <div style={{ color: "#9CA3AF", fontSize: 12 }}>Loading…</div>
                      ) : prods.map((p, i) => (
                        <div key={i} style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          padding: "5px 0", borderBottom: i < prods.length - 1 ? "1px solid #F3F4F6" : "none",
                        }}>
                          <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>
                            {p.productName || p.ProductName || p.pName}
                          </span>
                          <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                            {p.packing || p.Packing} · {p.qty ?? p.Qty ?? "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingTransferPopup;
